// src/main.ts
// Main application entrypoint, routing, and state orchestration

import './styles/theme.css';
import type { AppState, Household, ShoppingList, User } from './types';
import { api } from './services/api';
import { syncManager } from './services/sync';
import { Header } from './components/Header';
import { ListsView } from './components/ListsView';
import { ShoppingView } from './components/ShoppingView';
import { JoinView } from './components/JoinView';
import { AuthModal } from './components/AuthModal';
import { HouseholdModal } from './components/HouseholdModal';
import { InviteModal } from './components/InviteModal';
import { showToast } from './components/Toast';

class App {
  private state: AppState = {
    user: null,
    authenticated: false,
    households: [],
    currentHousehold: null,
    currentList: null,
    lists: [],
    knownItems: [],
    currentTab: 'active',
    activeScreen: 'lists',
    joinCode: null,
    pendingSync: false,
    theme: 'light',
  };

  private header!: Header;
  private listsView!: ListsView;
  private shoppingView!: ShoppingView;
  private joinView: JoinView | null = null;
  private authModal!: AuthModal;
  private householdModal!: HouseholdModal;
  private inviteModal!: InviteModal;

  private appMount: HTMLElement;
  private pollInterval: any = null;

  constructor() {
    this.appMount = document.getElementById('app')!;
    this.initTheme();
    this.checkJoinParam();
    this.initComponents();
    this.bootstrap();
  }

  private initTheme() {
    const saved = localStorage.getItem('lms_theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const theme = saved === 'dark' || (!saved && prefersDark) ? 'dark' : 'light';
    this.state.theme = theme;
    document.documentElement.setAttribute('data-theme', theme);
  }

  private toggleTheme() {
    const newTheme = this.state.theme === 'dark' ? 'light' : 'dark';
    this.state.theme = newTheme;
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('lms_theme', newTheme);
  }

  private checkJoinParam() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code) {
      this.state.joinCode = code.toUpperCase();
      this.state.activeScreen = 'join';
    }
  }

  private initComponents() {
    // Header
    this.header = new Header(this.state, {
      onLogoClick: () => this.navigateToLists(),
      onHouseholdClick: () => this.householdModal.open(this.state.currentHousehold, this.state.households),
      onAuthClick: () => this.authModal.open(this.state.user),
    });

    // Modals
    this.authModal = new AuthModal({
      onAuthSuccess: (user) => this.handleAuthSuccess(user),
      onLogoutSuccess: () => this.handleLogoutSuccess(),
      onThemeToggle: () => this.toggleTheme(),
      getTheme: () => this.state.theme,
    });

    this.householdModal = new HouseholdModal({
      onSelectHousehold: (h) => this.switchHousehold(h),
      onOpenInvite: (h) => this.inviteModal.open(h),
      onHouseholdUpdated: () => this.refreshHouseholds(),
    });

    this.inviteModal = new InviteModal();

    // Lists View
    this.listsView = new ListsView(this.state, {
      onSelectList: (list) => this.openList(list.id),
      onListCreated: (list) => {
        this.state.lists.unshift(list);
        this.openList(list.id);
      },
      onListDeleted: (id) => {
        this.state.lists = this.state.lists.filter((l) => l.id !== id);
        this.renderView();
      },
      onListArchived: () => this.loadLists(),
      onTabChange: (tab) => {
        this.state.currentTab = tab;
        this.renderView();
      },
    });

    // Shopping View
    this.shoppingView = new ShoppingView(this.state, {
      onBack: () => this.navigateToLists(),
      onListUpdated: () => {
        if (this.state.currentList) {
          this.openList(this.state.currentList.id);
        }
      },
    });
  }

  private async bootstrap() {
    try {
      const session = await api.checkSession();
      if (session.authenticated && session.user) {
        this.state.authenticated = true;
        this.state.user = session.user;
        this.state.households = session.households;

        // Pick active household from localStorage or first available
        const savedHhId = localStorage.getItem('lms_active_household_id');
        const active = this.state.households.find((h) => h.id === savedHhId) || this.state.households[0] || null;
        this.state.currentHousehold = active;

        if (active) {
          localStorage.setItem('lms_active_household_id', active.id);
          await this.loadLists();
          await this.loadSuggestions();
        }
      } else {
        // If not authenticated and no join code, open login modal after slight delay
        if (!this.state.joinCode) {
          setTimeout(() => this.authModal.open(null), 350);
        }
      }
    } catch (err) {
      console.warn('Session check fallback:', err);
    }

    this.renderView();
  }

  private async loadLists() {
    if (!this.state.currentHousehold) return;
    try {
      const res = await api.getLists(this.state.currentHousehold.id);
      this.state.lists = res.lists;
      this.renderView();
    } catch (err: any) {
      showToast('Failed to load lists: ' + err.message);
    }
  }

  private async loadSuggestions() {
    if (!this.state.currentHousehold) return;
    try {
      const res = await api.getSuggestions(this.state.currentHousehold.id);
      this.state.knownItems = res.items;
    } catch {}
  }

  private async refreshHouseholds() {
    try {
      const res = await api.getHouseholds();
      this.state.households = res.households;
      if (!this.state.households.some((h) => h.id === this.state.currentHousehold?.id)) {
        this.state.currentHousehold = this.state.households[0] || null;
      }
      this.header.update(this.state);
      await this.loadLists();
    } catch {}
  }

  private async switchHousehold(h: Household) {
    this.state.currentHousehold = h;
    localStorage.setItem('lms_active_household_id', h.id);
    this.state.currentList = null;
    this.state.activeScreen = 'lists';
    showToast(`Switched to ${h.name}`);
    this.header.update(this.state);
    await this.loadLists();
    await this.loadSuggestions();
  }

  private async openList(listId: string) {
    clearInterval(this.pollInterval);
    try {
      const res = await api.getList(listId);
      this.state.currentList = res.list;
      this.state.activeScreen = 'list-detail';
      this.renderView();

      // Background list polling (every 12 seconds)
      this.pollInterval = setInterval(async () => {
        if (this.state.activeScreen === 'list-detail' && this.state.currentList?.id === listId) {
          // Do not overwrite if offline ticks are pending flush
          if (!syncManager.hasPending(listId)) {
            try {
              const fresh = await api.getList(listId);
              this.state.currentList = fresh.list;
              this.shoppingView.update(this.state);
            } catch {}
          }
        }
      }, 12000);
    } catch (err: any) {
      showToast('Error opening list: ' + err.message);
    }
  }

  private navigateToLists() {
    clearInterval(this.pollInterval);
    this.state.currentList = null;
    this.state.activeScreen = 'lists';
    this.loadLists();
  }

  private async handleAuthSuccess(user: User) {
    this.state.authenticated = true;
    this.state.user = user;
    this.state.currentList = null;
    await this.refreshHouseholds();

    if (this.state.joinCode) {
      this.state.activeScreen = 'join';
    } else {
      this.state.activeScreen = 'lists';
    }
    this.renderView();
  }

  private handleLogoutSuccess() {
    clearInterval(this.pollInterval);
    this.state.authenticated = false;
    this.state.user = null;
    this.state.households = [];
    this.state.currentHousehold = null;
    this.state.lists = [];
    this.state.currentList = null;
    this.state.activeScreen = 'lists';
    this.renderView();
    setTimeout(() => this.authModal.open(null), 300);
  }

  private renderView() {
    this.header.update(this.state);
    this.appMount.innerHTML = '';
    this.appMount.appendChild(this.header.getElement());

    if (this.state.activeScreen === 'join' && this.state.joinCode) {
      this.joinView = new JoinView(this.state, this.state.joinCode, {
        onJoined: (h) => {
          window.history.replaceState({}, '', '/');
          this.state.joinCode = null;
          this.switchHousehold(h);
        },
        onCancel: () => {
          window.history.replaceState({}, '', '/');
          this.state.joinCode = null;
          this.navigateToLists();
        },
        onRequestLogin: () => this.authModal.open(null),
      });
      this.appMount.appendChild(this.joinView.getElement());
    } else if (this.state.activeScreen === 'list-detail' && this.state.currentList) {
      this.shoppingView.update(this.state);
      this.appMount.appendChild(this.shoppingView.getElement());
    } else {
      this.listsView.update(this.state);
      this.appMount.appendChild(this.listsView.getElement());
    }
  }
}

// Start app
new App();
